/**
 * Content Section
 *
 * Top topics bars + success rate gauge + publish stats.
 */

import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { CheckCircle2 } from 'lucide-react';
import type { ContentPerformance } from '../../../types/analytics';
import { StatCardSkeleton, ChartSkeleton } from './AnalyticsSkeleton';

const PLATFORM_COLORS = ['#a855f7', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

interface ContentSectionProps {
  content: ContentPerformance | null;
  loading: boolean;
}

export const ContentSection: React.FC<ContentSectionProps> = ({ content, loading }) => {
  if (loading || !content) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <StatCardSkeleton />
          <StatCardSkeleton />
        </div>
        <ChartSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-3 shadow-sm text-center">
          <p className="text-[10px] font-semibold uppercase text-gray-400">Generated</p>
          <p className="text-2xl font-extrabold text-gray-900 mt-1">{content.total_generated}</p>
        </div>
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-3 shadow-sm text-center">
          <p className="text-[10px] font-semibold uppercase text-gray-400">Success</p>
          <p className={`text-2xl font-extrabold mt-1 ${content.success_rate >= 90 ? 'text-emerald-600' : content.success_rate >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
            {content.success_rate}%
          </p>
        </div>
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-3 shadow-sm text-center">
          <p className="text-[10px] font-semibold uppercase text-gray-400">Published</p>
          <p className="text-2xl font-extrabold text-blue-600 mt-1">{content.publish_rate}%</p>
        </div>
      </div>

      {/* Top Topics Bar Chart */}
      {content.top_topics.length > 0 && (
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Top Topics</h3>
          <ResponsiveContainer width="100%" height={Math.max(150, content.top_topics.length * 30)}>
            <BarChart data={content.top_topics.slice(0, 8)} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis
                dataKey="topic"
                type="category"
                tick={{ fontSize: 9 }}
                width={100}
                tickFormatter={(v) => v.length > 15 ? v.slice(0, 15) + '...' : v}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              />
              <Bar dataKey="count" fill="#a855f7" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Publish by Platform */}
      {content.publish_by_platform.length > 0 && (
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Publish by Platform</h3>
          <div className="space-y-3">
            {content.publish_by_platform.map((p, idx) => (
              <div key={p.platform} className="flex items-center gap-3">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: PLATFORM_COLORS[idx % PLATFORM_COLORS.length] }}
                />
                <span className="text-xs font-medium text-gray-600 w-20 capitalize">{p.platform}</span>
                <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="flex h-full">
                    <div
                      className="h-full bg-emerald-400 rounded-l-full"
                      style={{ width: `${p.rate}%` }}
                    />
                    <div
                      className="h-full bg-red-300"
                      style={{ width: `${100 - p.rate}%` }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-1 text-[10px]">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                  <span className="text-gray-600">{p.successful}</span>
                  <span className="text-gray-300 mx-0.5">/</span>
                  <span className="text-gray-400">{p.total}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
